import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Hero from '../components/ui/Hero';
import Section from '../components/ui/Section';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Stats from '../components/ui/Stats';
import NewsletterSignup from '../components/forms/NewsletterSignup';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiUsers, FiMapPin, FiArrowRight, FiPlay, FiTarget, FiZap, FiStar } = FiIcons;

const Home = () => {
  const stats = [
    { value: 15, label: 'College Campuses', suffix: '+' },
    { value: 500, label: 'Students Reached', suffix: '+' },
    { value: 8, label: 'Cities in Florida' },
    { value: 25, label: 'Life Transformations' }
  ];

  const features = [
    {
      icon: FiUsers,
      title: 'Campus Outreach',
      description: 'Meeting students where they are with authentic conversations, prayer, and community on college campuses throughout Florida.',
      gradient: 'from-blue-500 to-cyan-500',
      link: '/campus-outreach'
    },
    {
      icon: FiMapPin,
      title: 'City Outreach',
      description: 'Taking the gospel to the streets with prayer walks, worship nights, and evangelism in downtown areas across Florida cities.',
      gradient: 'from-purple-500 to-pink-500',
      link: '/city-outreach'
    },
    {
      icon: FiHeart,
      title: 'Life Transformation',
      description: 'Witnessing God turn lives around 180 degrees through the power of His love, grace, and authentic community.',
      gradient: 'from-orange-500 to-red-500',
      link: '/testimonies'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      school: 'University of Florida',
      quote: 'Love 180 showed me that God\'s love isn\'t just theory - it\'s real and life-changing. My whole perspective did a complete 180.',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80',
      rating: 5
    },
    {
      name: 'Marcus T.',
      school: 'Florida State University',
      quote: 'I was struggling with purpose and direction. Through their campus outreach, I found hope and a community that truly cares.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80',
      rating: 5
    },
    {
      name: 'Jessica R.',
      school: 'Florida International University',
      quote: 'The authenticity of the Love 180 team drew me in. They live what they preach and it transformed my faith.',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80',
      rating: 5
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <Hero
        subtitle="Love 180 Ministry"
        title="Turning Lives Toward Jesus 180°"
        description="We're reaching college students and young adults across Florida through authentic relationships, bold faith, and the transforming power of Christ's love."
        primaryCta={{ text: 'Join an Outreach', href: '/get-involved' }}
        secondaryCta={{ text: 'Support Our Mission', href: '/donate' }}
        backgroundImage="https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80"
      />

      {/* Stats Section */}
      <Section className="-mt-16 relative z-10">
        <Card className="p-8 shadow-2xl bg-white">
          <Stats stats={stats} />
        </Card>
      </Section>

      {/* Features Section */}
      <Section>
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">Our Mission</span>
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mt-4 mb-6">
              Real Faith, Real Impact
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              We believe in the power of authentic relationships to transform lives. Through campus and city outreach across Florida, we're seeing God do amazing things.
            </p>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className="p-8 h-full group cursor-pointer" gradient>
                <div className={`w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <SafeIcon icon={feature.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-neutral-900">{feature.title}</h3>
                <p className="text-neutral-600 mb-6 leading-relaxed">{feature.description}</p>
                <Link 
                  to={feature.link} 
                  className="text-primary-600 font-semibold inline-flex items-center group-hover:gap-3 gap-2 transition-all"
                >
                  Learn More 
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                </Link>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Campus Map Section */}
      <Section background="gray">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">Our Reach</span>
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mt-4 mb-6">
              Reaching Florida Campuses
            </h2>
            <p className="text-lg text-neutral-600 mb-8">
              From Gainesville to Miami, we're building authentic relationships with students across Florida's major universities and colleges. Each campus presents unique opportunities to share Christ's love.
            </p>
            <div className="space-y-4 mb-8">
              {[
                'University of Florida - Gainesville',
                'Florida State University - Tallahassee',
                'Florida International University - Miami',
                'University of Central Florida - Orlando',
                '+ 11 more campuses'
              ].map((campus, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-center space-x-3 group"
                >
                  <div className="w-2 h-2 bg-primary-600 rounded-full group-hover:scale-150 transition-transform" />
                  <span className="text-neutral-700 group-hover:text-primary-600 transition-colors">{campus}</span>
                </motion.div>
              ))}
            </div>
            <div className="flex justify-center sm:justify-start">
              <Button variant="primary" size="lg">
                <SafeIcon icon={FiTarget} className="w-5 h-5 mr-2" />
                View All Campuses
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <Card className="p-8 shadow-2xl">
              <div className="w-full h-80 bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzAwMCIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30" />
                <div className="text-center relative z-10">
                  <SafeIcon icon={FiMapPin} className="w-16 h-16 text-primary-600 mx-auto mb-4" />
                  <p className="text-neutral-600 font-medium text-lg">Interactive Campus Map</p>
                  <p className="text-sm text-neutral-500 mt-2">Coming Soon</p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </Section>

      {/* Testimonials Section */}
      <Section>
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">Success Stories</span>
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mt-4 mb-6">
              Lives Transformed
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Hear from students whose lives have been turned around 180 degrees through encounters with Jesus.
            </p>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className="p-8 h-full" gradient>
                <div className="flex items-center mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-14 h-14 rounded-full object-cover ring-4 ring-primary-100 mr-4"
                  />
                  <div className="flex-1">
                    <h4 className="font-bold text-neutral-900">{testimonial.name}</h4>
                    <p className="text-sm text-neutral-600">{testimonial.school}</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-neutral-700 italic leading-relaxed">"{testimonial.quote}"</p>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="flex justify-center">
          <Link to="/testimonies">
            <Button variant="outline" size="lg">
              <SafeIcon icon={FiPlay} className="w-5 h-5 mr-2" />
              Watch More Stories
            </Button>
          </Link>
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="gray">
        <Card className="p-12 text-center bg-gradient-to-br from-primary-600 to-primary-800 text-white shadow-2xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <SafeIcon icon={FiZap} className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Make an Impact?</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              Join us in reaching the next generation for Christ. Whether through volunteering, prayer, or financial support, you can be part of life transformation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/get-involved" className="w-full sm:w-auto">
                <Button variant="secondary" size="lg" className="shadow-xl w-full sm:w-auto">
                  Get Involved Today
                </Button>
              </Link>
              <Link to="/donate" className="w-full sm:w-auto">
                <Button 
                  variant="ghost" 
                  size="lg" 
                  className="text-white border-2 border-white hover:bg-white hover:text-primary-600 w-full sm:w-auto"
                >
                  Support the Mission
                </Button>
              </Link>
            </div>
          </motion.div>
        </Card>
      </Section>

      {/* Newsletter Section */}
      <Section>
        <div className="max-w-2xl mx-auto">
          <NewsletterSignup />
        </div>
      </Section>
    </div>
  );
};

export default Home;